import { useEffect } from "react";

/**
 * useProfileLoginPrefetch
 * -----------------------
 * On login, prefetches:
 *  1) "info" (latest metadata timestamp) immediately (fast gate support)
 *  2) Profile JSON only if the recert gate suggests we might need it
 *
 * This reduces perceived latency when the user immediately navigates to
 * the confirm metadata route.
 *
 * @param {object} args
 * @param {string|null|undefined} args.uid
 * @param {(args: { uid: string, ttlMs?: number }) => Promise<any>} args.ensureProfileInfo
 * @param {(args: { uid: string, ttlMs?: number }) => Promise<any>} args.ensureProfile
 * @param {(uid: string) => any} [args.getCachedProfileInfo]
 * @param {number} [args.ttlMs] Cache TTL for prefetch calls
 * @param {number} [args.recertMs] Recert threshold; if older, prefetch JSON
 */
export default function useProfileLoginPrefetch({
  uid,
  ensureProfileInfo,
  ensureProfile,
  getCachedProfileInfo,
  ttlMs = 10 * 60 * 1000,
  recertMs = 3 * 30 * 24 * 60 * 60 * 1000, // pragmatic 90 days
}) {
  useEffect(() => {
    if (!uid) return;

    const t = window.setTimeout(() => {
      // Always prefetch info first (fast)
      ensureProfileInfo({ uid, ttlMs }).catch(() => {});

      // Conditionally prefetch JSON if older than threshold
      (async () => {
        try {
          const cached =
            typeof getCachedProfileInfo === "function"
              ? getCachedProfileInfo(uid)
              : null;

          const info =
            cached?.status === "ready"
              ? cached
              : await ensureProfileInfo({ uid, ttlMs });

          if (info?.latestMs && Date.now() - info.latestMs > recertMs) {
            ensureProfile({ uid, ttlMs }).catch(() => {});
          }
        } catch {
          // ignore
        }
      })();
    }, 0);

    return () => window.clearTimeout(t);
  }, [
    uid,
    ensureProfileInfo,
    ensureProfile,
    getCachedProfileInfo,
    ttlMs,
    recertMs,
  ]);
}
